/**
 * One icon per resource type, from lucide-react (the family ds/ uses).
 *
 * Replaces the emoji maps several pages kept for the same job. The five core
 * types use the same icons as the sidebar (Layout.tsx), so a type reads the same
 * in the nav, a table row and an access-request card. Cloud service types get the
 * nearest Lucide shape; anything unknown falls back to Package.
 */
import {
  Archive, Boxes, ChartLine, Cloud, Container, Database, DoorOpen, Earth, FolderOpen,
  Globe, HardDrive, Inbox, Key, KeyRound, LockKeyhole, Megaphone, MemoryStick, Monitor,
  Network, Package, Radar, Scale, Search, Server, Sheet, Sprout, SquareTerminal, Zap,
  type LucideIcon, type LucideProps,
} from 'lucide-react';

const ICONS: Record<string, LucideIcon> = {
  database: Database,
  http_proxy: Globe,
  ssh: SquareTerminal,
  k8s: Container,
  desktop: Monitor,

  aws_account: Cloud,
  aws_ec2: Server,
  aws_s3: Archive,
  aws_route53: Earth,
  aws_eks: Container,
  aws_ecr: Package,
  aws_ecs: Boxes,
  aws_lambda: Zap,
  aws_rds: Database,
  aws_cloudfront: Globe,
  aws_lightsail: Server,
  aws_elasticbeanstalk: Sprout,
  aws_efs: FolderOpen,
  aws_dynamodb: Sheet,
  aws_elasticache: MemoryStick,
  aws_redshift: ChartLine,
  aws_opensearch: Search,
  aws_vpc: Network,
  aws_elb: Scale,
  aws_apigateway: DoorOpen,
  aws_iam: LockKeyhole,
  aws_secretsmanager: KeyRound,
  aws_kms: Key,
  aws_sqs: Inbox,
  aws_sns: Megaphone,
  aws_cloudwatch: Radar,

  azure_vm: Server,
  azure_storage: Package,
  azure_acr: Boxes,

  hetzner_server: Server,
  hetzner_volume: HardDrive,
  hetzner_loadbalancer: Scale,
};

/** The Lucide icon for a resource type (`database`, `k8s`, `aws_s3` …). */
export function resourceIcon(type: string | null | undefined): LucideIcon {
  return (type && ICONS[type]) || Package;
}

/** A cloud service tile: `aws` + `s3` finds aws_s3; a bare type is tried too. */
export function serviceIcon(provider: string, serviceType: string | null | undefined): LucideIcon {
  const t = (serviceType ?? '').toLowerCase();
  return ICONS[`${provider}_${t}`] || ICONS[t] || Package;
}

const CATEGORY_ICONS: Record<string, LucideIcon> = {
  compute: Server,
  storage: FolderOpen,
  networking: Network,
  containers: Container,
  database: Database,
  cdn: Earth,
  security: LockKeyhole,
  messaging: Inbox,
  monitoring: Radar,
};

/** A service-catalog category heading's icon. */
export function categoryIcon(category: string): LucideIcon {
  return CATEGORY_ICONS[category] || Package;
}

/** The icon, decorative: the type's name is always written beside it. */
export function ResourceTypeIcon({ type, className = 'h-4 w-4', ...rest }: { type: string | null | undefined } & LucideProps) {
  const Icon = resourceIcon(type);
  return <Icon aria-hidden="true" className={className} {...rest} />;
}

/**
 * The d2d Feature Icon Tile (docs/design/d2d/d2d.md): an 8px-radius square in the
 * accent tag's Mist Violet with the type's stroke icon. Where the pages drew a
 * large emoji beside a resource, role or session name.
 */
export function IconTile({ icon: Icon, size = 32 }: { icon: LucideIcon; size?: 28 | 32 | 36 }) {
  const box = size === 28 ? 'h-7 w-7' : size === 36 ? 'h-9 w-9' : 'h-8 w-8';
  return (
    <span className={`flex ${box} flex-shrink-0 items-center justify-center rounded-[var(--radius-card)] bg-[var(--accent-tag-bg)] text-[var(--accent-tag-text)]`}>
      <Icon aria-hidden="true" className="h-4 w-4" />
    </span>
  );
}

export function ResourceTypeTile({ type, size }: { type: string | null | undefined; size?: 28 | 32 | 36 }) {
  return <IconTile icon={resourceIcon(type)} size={size} />;
}
